import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { inquiriesAPI, formatApiErrorDetail } from '../../lib/api';
import { MagnifyingGlass, Eye, Trash, Envelope, Phone, CheckCircle, Clock, EnvelopeOpen } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const INQUIRY_TYPES = [
  { value: 'contact', label: 'Contact Us' },
  { value: 'support', label: 'Support/Contribution' },
  { value: 'contribution_interest', label: 'Contribution Interest' }
];

const STATUSES = [
  { value: 'new', label: 'New', icon: Envelope, color: 'bg-blue-100 text-blue-800' },
  { value: 'read', label: 'Read', icon: EnvelopeOpen, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'responded', label: 'Responded', icon: CheckCircle, color: 'bg-green-100 text-green-800' }
];

const InquiriesPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewingInquiry, setViewingInquiry] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await inquiriesAPI.getAll();
      setInquiries(response.data);
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (inquiry) => {
    setViewingInquiry(inquiry);
    // Mark as read if new
    if (inquiry.status === 'new') {
      try {
        await inquiriesAPI.update(inquiry.id, { status: 'read' });
        fetchData();
      } catch (error) {
        console.log('Error updating status');
      }
    }
  };

  const handleStatusChange = async (inquiry, newStatus) => {
    try {
      await inquiriesAPI.update(inquiry.id, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDelete = async (inquiry) => {
    if (!window.confirm(`Delete this inquiry from ${inquiry.name}?`)) return;
    try {
      await inquiriesAPI.delete(inquiry.id);
      toast.success('Inquiry deleted');
      setViewingInquiry(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const filteredInquiries = inquiries.filter(i => {
    const matchesSearch = i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          i.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          i.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || i.inquiry_type === filterType;
    const matchesStatus = filterStatus === 'all' || i.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const newCount = inquiries.filter(i => i.status === 'new').length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="inquiries-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Messages & Inquiries</h1>
            <p className="text-[#4A453E]">
              Contact form submissions and support requests
              {newCount > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                  {newCount} new
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A847A]" size={20} />
            <Input
              placeholder="Search by name, email, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="search-inquiries"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 rounded-none">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {INQUIRY_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 rounded-none">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-[#8A847A]">Loading...</div>
        ) : filteredInquiries.length === 0 ? (
          <div className="text-center py-12 text-[#8A847A]">No inquiries found</div>
        ) : (
          <div className="border border-[#D8D2C4] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F5F2EB]">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold uppercase text-[#8A847A]">Status</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase text-[#8A847A]">From</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase text-[#8A847A]">Subject</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase text-[#8A847A]">Type</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase text-[#8A847A]">Date</th>
                  <th className="text-center p-4 text-xs font-semibold uppercase text-[#8A847A]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D8D2C4]">
                {filteredInquiries.map(inquiry => {
                  const status = STATUSES.find(s => s.value === inquiry.status) || STATUSES[0];
                  const StatusIcon = status.icon;
                  return (
                    <tr key={inquiry.id} className={`hover:bg-[#F5F2EB]/50 ${inquiry.status === 'new' ? 'bg-blue-50/30' : ''}`}>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${status.color}`}>
                          <StatusIcon size={14} />
                          {status.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-[#1A1614]">{inquiry.name}</div>
                        <div className="text-sm text-[#8A847A]">{inquiry.email}</div>
                      </td>
                      <td className="p-4 text-[#4A453E] max-w-xs truncate">
                        {inquiry.subject || inquiry.message?.substring(0, 50) + '...'}
                      </td>
                      <td className="p-4">
                        <span className="text-xs text-[#8A847A] capitalize">
                          {INQUIRY_TYPES.find(t => t.value === inquiry.inquiry_type)?.label || inquiry.inquiry_type}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-[#8A847A]">
                        {inquiry.created_at ? new Date(inquiry.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => handleView(inquiry)}
                            className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                            title="View"
                          >
                            <Eye size={18} />
                          </button>
                          <button 
                            onClick={() => handleDelete(inquiry)}
                            className="p-2 hover:bg-red-50 text-red-600"
                            title="Delete"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* View Modal */}
        <Dialog open={!!viewingInquiry} onOpenChange={() => setViewingInquiry(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Message Details</DialogTitle>
            </DialogHeader>
            {viewingInquiry && (
              <div className="space-y-6" data-testid="inquiry-details">
                {/* Header Info */}
                <div className="flex justify-between items-start pb-4 border-b border-[#D8D2C4]">
                  <div>
                    <h3 className="font-heading text-xl text-[#1A1614]">{viewingInquiry.name}</h3>
                    <div className="flex items-center gap-2 text-[#4A453E] mt-1">
                      <Envelope size={16} />
                      <a href={`mailto:${viewingInquiry.email}`} className="hover:text-[#7B1818]">
                        {viewingInquiry.email}
                      </a>
                    </div>
                    {viewingInquiry.phone && (
                      <div className="flex items-center gap-2 text-[#4A453E] mt-1">
                        <Phone size={16} />
                        <a href={`tel:${viewingInquiry.phone}`} className="hover:text-[#7B1818]">
                          {viewingInquiry.phone}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-[#8A847A] capitalize px-2 py-1 bg-[#F5F2EB]">
                      {INQUIRY_TYPES.find(t => t.value === viewingInquiry.inquiry_type)?.label || viewingInquiry.inquiry_type}
                    </span>
                    <p className="text-xs text-[#8A847A] mt-2">
                      {viewingInquiry.created_at ? new Date(viewingInquiry.created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>

                {/* Subject */}
                {viewingInquiry.subject && (
                  <div>
                    <p className="text-xs text-[#8A847A] mb-1">Subject</p>
                    <p className="font-medium text-[#1A1614]">{viewingInquiry.subject}</p>
                  </div>
                )}

                {/* Message */}
                <div>
                  <p className="text-xs text-[#8A847A] mb-1">Message</p>
                  <div className="bg-[#F5F2EB] p-4 text-[#4A453E] whitespace-pre-wrap">
                    {viewingInquiry.message}
                  </div>
                </div>

                {/* Status Update */}
                <div className="flex items-center justify-between pt-4 border-t border-[#D8D2C4]">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#8A847A]">Mark as:</span>
                    {STATUSES.map(s => (
                      <button
                        key={s.value}
                        onClick={() => handleStatusChange(viewingInquiry, s.value)}
                        className={`px-3 py-1 text-xs border transition-colors ${
                          viewingInquiry.status === s.value 
                            ? s.color + ' border-current' 
                            : 'border-[#D8D2C4] hover:bg-[#F5F2EB]'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(viewingInquiry)}
                    className="rounded-none"
                  >
                    <Trash size={16} className="mr-1" /> Delete
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default InquiriesPage;
